import type { ElementProps, ImageProps as MantineImageProps } from '@mantine/core';
import { Image as MantineImage } from '@mantine/core';

import type { LockedProps } from '../types';

// Mantine's ImageProps extends only BoxProps/StylesApiProps — it carries none of the `img`
// element's own attributes, so `alt` and friends have to be intersected back in (minus the style
// escape hatches). Previously a `PolymorphicComponentProps<any, …>` wrapper supplied them, which
// also re-admitted every locked prop; no call site renders this as another element.
// `src` comes from Mantine only: the img attribute is `string | undefined`, and intersecting that
// would reject the `src={null}` Mantine documents for falling back to `fallbackSrc`.
export type ImageProps = Omit<MantineImageProps, LockedProps> &
  Omit<ElementProps<'img'>, 'className' | 'style' | 'src'>;

// Mantine v8's Image root forces `width: 100%`, overriding the legacy `width`/`height`
// HTML attributes. Map them onto the `w`/`h` style props (applied inline, so they win
// over the stylesheet) so fixed-size images keep their dimensions.
const toSize = (value: string | number | undefined) =>
  typeof value === 'string' && /^\d+(\.\d+)?$/.test(value) ? Number(value) : value;

export const Image = ({ width, height, w, h, ...props }: ImageProps) => {
  return <MantineImage w={w ?? toSize(width)} h={h ?? toSize(height)} {...props} />;
};
